import React, { useState, useEffect, useRef } from "react";
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  DragOverlay,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {
  usePhotoDragAndDrop,
  STAGING_AREA_ID,
} from "../hooks/usePhotoDragAndDrop";

import ListingCard from "../components/ListingCard/ListingCard";
import PasteDiscogsBatch from "../components/PasteDiscogsBatch/PasteDiscogsBatch";
import PhotoAlbumPickerButton from "../components/PhotoAlbumPickerButton/PhotoAlbumPickerButton";
import PhotoStagingArea from "../components/PhotoStagingArea/PhotoStagingArea";
import QuickDropZones from "../components/QuickDropZones/QuickDropZones";
import DraggableImage from "../components/DraggableImage/DraggableImage";
import ClearBatchButton from "../components/ClearBatchButton/ClearBatchButton";

import { parseDiscogsData } from "../utils/parseDiscogsData";
import { applyGradingDefaults } from "../utils/ebayUtils";
import { generateDescription } from "../utils/generateDescription";
import { getApiUrl } from "../lib/api";
import { matchPhotos } from "../utils/matchPhotos";

const BatchListingManager = () => {
  const [listings, setListings] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [albumName, setAlbumName] = useState("");
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [containers, setContainers] = useState({ [STAGING_AREA_ID]: [] });

  const [batchId, setBatchId] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [fetchTimestamp, setFetchTimestamp] = useState(Date.now());

  const [isMatching, setIsMatching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPricing, setIsPricing] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const { activePhoto, handleDragStart, handleDragEnd } = usePhotoDragAndDrop(
    containers,
    setContainers
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const pasteRef = useRef(null);

  useEffect(() => {
    const updated = {};
    listings.forEach((listing) => {
      // Preserve existing images in containers, don't overwrite with empty uploadedImages
      updated[listing.releaseId] = listing.formData.uploadedImages?.length > 0 
        ? listing.formData.uploadedImages 
        : (containers[listing.releaseId] || []);
    });

    setContainers((prev) => ({
      ...prev,
      ...updated,
      [STAGING_AREA_ID]: prev[STAGING_AREA_ID] || [],
    }));
  }, [listings]);

  useEffect(() => {
    if (toastMessage) {
      const timeout = setTimeout(() => setToastMessage(""), 4000);
      return () => clearTimeout(timeout);
    }
  }, [toastMessage]);

  const handleListingsFetched = (fetchedData) => {
    // Update fetch timestamp to force component re-creation
    setFetchTimestamp(Date.now());
    
    // Reset validation errors on fresh fetch
    setShowValidationErrors(false);
    
    // Clear all existing photos from containers when refetching
    const releaseIds = fetchedData.map(item => item.releaseId);
    setContainers(prev => {
      const updated = { ...prev };
      // Remove container entries for all refetched releases to ensure clean slate
      releaseIds.forEach(releaseId => {
        delete updated[releaseId];
      });
      return updated;
    });
    
    const parsed = fetchedData.map((item) => {
      if (item.error) {
        return { releaseId: item.releaseId, error: item.error };
      }
      const raw = item.rawData;
      const condition = "USED";
      let parsedData = parseDiscogsData(raw, condition);
      const grading = applyGradingDefaults(parsedData, condition);
      const description = generateDescription({
        ...parsedData,
        ...grading,
        condition,
      });
      return {
        releaseId: item.releaseId,
        originalReleaseData: raw,
        formData: {
          ...parsedData,
          ...grading,
          condition,
          itemCondition: condition, // ✅ ADD THIS LINE
          description,
          uploadedImages: [],
        },
      };
    });
    setListings(parsed);
  };

  const updateListing = (index, newFormData) => {
    const updated = [...listings];
    updated[index] = { ...updated[index], formData: newFormData };
    setListings(updated);
  };

  const handleMatchPhotos = async () => {
    try {
      setIsMatching(true);
      setToastMessage("");

      const stagedPhotos = (containers[STAGING_AREA_ID] || []).map(
        (photo) => photo.baseUrl
      );

      const listingData = listings.map((listing) => ({
        id: listing.releaseId,
        coverUrl: listing.formData.imageUrl,
      }));

      const payload = {
        listings: listingData,
        photos: stagedPhotos,
      };

      const matchResult = await matchPhotos(payload);
      const { matched = {}, unmatched = [] } = matchResult;

      setContainers((prev) => {
        const updated = { ...prev };

        for (const [listingId, photoUrls] of Object.entries(matched)) {
          const existing = updated[listingId] || [];

          const newPhotos = photoUrls.map((url) => {
            const filename = url.split("/").pop();
            return {
              photoId: `match-${listingId}-${filename}`,
              baseUrl: url,
              thumbSrc: url,
            };
          });

          updated[listingId] = [...existing, ...newPhotos];
        }

        updated[STAGING_AREA_ID] = (prev[STAGING_AREA_ID] || []).filter(
          (photo) => unmatched.includes(photo.baseUrl)
        );

        return updated;
      });

      setToastType("success");
      setToastMessage("✅ Photos matched and assigned.");
    } catch (err) {
      console.error("❌ Photo matching failed:", err);
      setToastType("error");
      setToastMessage("❌ Photo matching failed. Check console for details.");
    } finally {
      setIsMatching(false);
    }
  };

  const handleSubmitAll = async () => {
    try {
      console.log("🔍 Before submission - containers:", containers);
      console.log("🔍 Before submission - listings:", listings);
      
      setShowValidationErrors(true); // Show validation errors when submission is attempted
      setIsSubmitting(true);
      setToastMessage("");
      setSubmissionSuccess(false);

      const listingsWithImageUrls = listings.map((l, index) => {
        const uploadedImages = (containers[l.releaseId] || []).map((p) => ({
          url: p.baseUrl,
        }));

        const { uploadedImages: _, ...restFormData } = l.formData;
        
        console.log(`🔍 Listing ${index} (${l.releaseId}) price:`, restFormData.price);
        console.log(`🔍 TITLE DEBUG Frontend - title:`, restFormData.title);
        console.log(`🔍 TITLE DEBUG Frontend - fullTitle:`, restFormData.fullTitle);
        console.log(`🔍 TITLE DEBUG Frontend - releaseTitle:`, restFormData.releaseTitle);

        return {
          ...restFormData,
          releaseId: l.releaseId,
          imageUrls: uploadedImages.map((img) => img.url),
        };
      });

      const res = await fetch(getApiUrl("/api/ebay/batch-listing"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listings: listingsWithImageUrls }),
      });

      const data = await res.json();

      if (res.ok) {
        const resultMap = new Map(data.results.map((r) => [r.releaseId, r]));
        setListings((prev) =>
          prev.map((l) => ({
            ...l,
            result: resultMap.get(l.releaseId) || null,
          }))
        );

        const total = data.results.length;
        const successCount = data.results.filter((r) => r.success).length;
        const failCount = total - successCount;

        setSubmissionSuccess(true);

        setToastType("success");
        setToastMessage(
          `✅ ${successCount} listings submitted successfully. ${
            failCount > 0 ? `${failCount} failed.` : ""
          }`
        );
      } else {
        setToastType("error");
        setToastMessage("❌ Failed to submit some listings.");
        console.error("Batch error:", data);
      }
    } catch (err) {
      console.error("❌ Submit error:", err);
      setToastType("error");
      setToastMessage("❌ Listing submission failed. See console for details.");
    } finally {
      console.log("🔍 After submission - containers:", containers);
      console.log("🔍 After submission - listings:", listings);
      setIsSubmitting(false);
    }
  };

  const handleReturnToStaging = (photoId, sourceContainerId) => {
    setContainers((prev) => {
      const sourceItems = prev[sourceContainerId] || [];
      const itemToMove = sourceItems.find((p) => p.photoId === photoId);
      if (!itemToMove) return prev;
      if (prev[STAGING_AREA_ID].some((p) => p.photoId === photoId)) {
        return {
          ...prev,
          [sourceContainerId]: sourceItems.filter((p) => p.photoId !== photoId),
        };
      }
      return {
        ...prev,
        [sourceContainerId]: sourceItems.filter((p) => p.photoId !== photoId),
        [STAGING_AREA_ID]: [...prev[STAGING_AREA_ID], itemToMove],
      };
    });
  };

  const handleRemoveFromStaging = async (photoId) => {
    try {
      // Find the photo to get its details for backend deletion
      const photo = (containers[STAGING_AREA_ID] || []).find(p => p.photoId === photoId);

      if (photo && photo.baseUrl && photo.baseUrl.includes('supabase.co')) {
        // Delete from Supabase if it's a Supabase-hosted image
        const response = await fetch(getApiUrl('/api/delete-single-photo'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            photoId: photoId,
            batchId: batchId
          })
        });

        if (!response.ok) {
          console.warn('Failed to delete photo from Supabase:', photoId);
        } else {
          console.log('✅ Photo deleted from Supabase:', photoId);
        }
      }

      // Remove from frontend state
      setContainers((prev) => ({
        ...prev,
        [STAGING_AREA_ID]: prev[STAGING_AREA_ID].filter(p => p.photoId !== photoId)
      }));

      setToastMessage("📸 Photo removed");
      setToastType("success");

    } catch (error) {
      console.error('Error removing photo:', error);
      setToastMessage("❌ Failed to remove photo");
      setToastType("error");
    }
  };

  const handlePickerSelection = async ({ sessionId }) => {
    setLoadingPhotos(true);
    setAlbumName("Importing photos...");

    try {
      const pickerRes = await fetch(
        getApiUrl(
          `/api/picker/media-items?sessionId=${encodeURIComponent(sessionId)}`
        ),
        { credentials: "include" }
      );
      if (!pickerRes.ok)
        throw new Error("Failed to fetch selected photos from picker.");
      const pickerData = await pickerRes.json();
      const mediaItems = (pickerData.mediaItems || []).sort(
        (a, b) => new Date(a.createTime) - new Date(b.createTime)
      );

      if (mediaItems.length === 0) {
        setAlbumName("");
        setLoadingPhotos(false);
        return;
      }

      const newBatchId = `batch-${Date.now()}`;
      setBatchId(newBatchId);
      setUploadProgress({ current: 0, total: mediaItems.length });
      
      // Better progress messaging
      const estimatedMinutes = Math.ceil(mediaItems.length / 240); // ~4 photos/sec optimized
      setAlbumName(`Uploading ${mediaItems.length} photos (est. ${estimatedMinutes} min)...`);
      
      console.log(`⏱️ Estimated upload time: ~${estimatedMinutes} minutes for ${mediaItems.length} photos`);

      const photosToUpload = mediaItems
        .map((item) => ({
          photoId: item.id,
          googleUrl: item.mediaFile?.baseUrl || item.baseUrl || item.url,
        }))
        .filter((p) => p.photoId && p.googleUrl);

      // Start SSE connection for progress updates
      const eventSource = new EventSource(
        getApiUrl(`/api/upload-progress/${newBatchId}`),
        { withCredentials: true }
      );

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'progress') {
            setUploadProgress({ current: data.current, total: data.total });

            if (data.completed) {
              setAlbumName("Upload Complete");
              eventSource.close();
            } else {
              const percent = Math.round((data.current / data.total) * 100);
              setAlbumName(`Uploading ${data.current}/${data.total} photos (${percent}%)...`);
            }
          }
        } catch (err) {
          console.error('Error parsing SSE data:', err);
        }
      };

      eventSource.onerror = (error) => {
        console.log('SSE connection error:', error);
        eventSource.close();
      };

      // Start the upload
      const uploadRes = await fetch(
        getApiUrl("/api/batch-upload-from-google"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            photos: photosToUpload,
            batchId: newBatchId,
          }),
        }
      );

      if (!uploadRes.ok) {
        eventSource.close();
        throw new Error("Backend failed to process the photo batch.");
      }

      const uploadData = await uploadRes.json();

      // Ensure SSE connection is closed
      eventSource.close();

      // Show warning if some photos failed to upload
      if (uploadData.failedCount > 0) {
        console.warn(`⚠️ ${uploadData.failedCount} photos failed to upload after retries:`, uploadData.failedPhotoIds);
        alert(`Warning: ${uploadData.failedCount} photos failed to upload and will not be available for listing. Check the console for details.`);
      }

      const finalPhotoObjects = uploadData.uploadedPhotos.map((p) => ({
        photoId: p.photoId,
        baseUrl: p.publicUrl,
        thumbSrc: p.publicUrl,
        createTime: p.createTime,
      }));

      setContainers((prev) => ({
        ...prev,
        [STAGING_AREA_ID]: [...prev[STAGING_AREA_ID], ...finalPhotoObjects],
      }));

      // Final cleanup - these may already be set by SSE, but ensure they're correct
      setAlbumName("Upload Complete");
      setUploadProgress({ current: uploadData.uploadedPhotos.length, total: photosToUpload.length });
    } catch (err) {
      setToastType("error");
      setToastMessage("❌ Photo upload failed. See console for details.");
      console.error("Upload Error:", err);
      setAlbumName("Upload Failed");
      setUploadProgress({ current: 0, total: 0 });
    } finally {
      setLoadingPhotos(false);
    }
  };

  // Helper function to convert long condition names to short codes
  const convertConditionToShortCode = (condition) => {
    if (!condition) return "NM";
    
    const conditionMap = {
      "Mint (M)": "M",
      "Near Mint (NM or M-)": "NM", 
      "Excellent (EX)": "EX",
      "Very Good Plus (VG+)": "VG+",
      "Very Good (VG)": "VG",
      "Good Plus (G+)": "G+",
      "Good (G)": "G",
      "Fair (F)": "F",
      "Worn (W)": "P" // Map Worn to Poor
    };
    
    return conditionMap[condition] || "NM";
  };

  const handleGetPricing = async () => {
    setIsPricing(true);
    setToastMessage("Getting AI pricing for all records...");
    setToastType("info");

    try {
      // Map listings to pricing engine format
      const pricingRequests = listings.map(listing => ({
        // From Discogs metadata (use raw Discogs data, not processed eBay title)
        artist: Array.isArray(listing.formData.artist) ? listing.formData.artist : [listing.formData.artist],
        title: listing.formData.fullTitle, // Use raw Discogs title for search, not processed eBay title
        label: listing.formData.label || null,
        catalog_number: Array.isArray(listing.formData.catalogNumber) 
          ? listing.formData.catalogNumber[0] 
          : (listing.formData.catalogNumber || null),
        upc: listing.formData.upc || null,
        format: "LP", // Default to LP for vinyl records
        year: listing.formData.releaseYear ? parseInt(listing.formData.releaseYear) : null,
        country: listing.formData.country || null,
        vinyl_color: listing.formData.colorway || null,
        
        // Condition settings (convert long form to short codes)
        condition_media: convertConditionToShortCode(listing.formData.conditionMedia) || "NM",
        condition_sleeve: convertConditionToShortCode(listing.formData.conditionSleeve) || "NM", 
        item_condition: listing.formData.itemCondition || "USED",
        
        // IDs for reference
        discogs_release_id: parseInt(listing.releaseId),
        
        // Options
        enable_discogs_data: true
      }));

      console.log('🎵 Sending pricing requests:', pricingRequests);
      console.log('🔍 First request sample:', JSON.stringify(pricingRequests[0], null, 2));

      // Call backend pricing endpoint
      const response = await fetch(getApiUrl('/api/pricing/batch'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(pricingRequests)
      });

      if (!response.ok) {
        throw new Error(`Pricing request failed: ${response.statusText}`);
      }

      const pricingResults = await response.json();
      console.log('💰 Received pricing results:', pricingResults);

      // Update each listing with pricing data
      const updatedListings = listings.map((listing, index) => {
        const pricingResult = pricingResults[index];
        if (pricingResult && pricingResult.success) {
          return {
            ...listing,
            formData: {
              ...listing.formData,
              pricingData: pricingResult.result,
              price: pricingResult.result.suggested_price
            }
          };
        }
        return listing;
      });

      setListings(updatedListings);
      
      const successCount = pricingResults.filter(r => r.success).length;
      setToastMessage(`✅ Pricing complete! ${successCount}/${listings.length} records priced successfully.`);
      setToastType("success");

    } catch (error) {
      console.error('❌ Pricing error:', error);
      setToastMessage(`❌ Pricing failed: ${error.message}`);
      setToastType("error");
    } finally {
      setIsPricing(false);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="px-4 py-10 max-w-4xl mx-auto text-white">
        <h1 className="text-3xl font-bold mb-6 text-center">
          Drop Groove – Batch Listing
        </h1>
        <div className="bg-zinc-900 border border-zinc-700 p-6 rounded-2xl shadow-xl mb-10">
          <PasteDiscogsBatch
            ref={pasteRef}
            onListingsFetched={handleListingsFetched}
          />
        </div>

        {listings.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center">
            No listings yet. Paste Discogs URLs above.
          </p>
        ) : (
          <>
            <div className="space-y-6">
              {listings.map((listing, idx) => (
                <ListingCard
                  key={`${listing.releaseId}-${fetchTimestamp}`}
                  id={listing.releaseId}
                  formData={listing.formData}
                  setFormData={(newData) => updateListing(idx, newData)}
                  images={containers[listing.releaseId] || []}
                  onRemoveImage={(photoId) =>
                    handleReturnToStaging(photoId, listing.releaseId)
                  }
                  originalReleaseData={listing.originalReleaseData}
                  showValidationErrors={showValidationErrors}
                />
              ))}
            </div>
            <div className="text-center mt-10 space-x-4">
              <PhotoAlbumPickerButton
                onSelection={handlePickerSelection}
                disabled={loadingPhotos}
              />
              <button
                onClick={handleGetPricing}
                disabled={loadingPhotos || isPricing}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                {isPricing ? "Getting Prices..." : "Get AI Pricing"}
              </button>
            </div>
          </>
        )}

        {listings.length > 0 && (
          <>
            <PhotoStagingArea
              id={STAGING_AREA_ID}
              photos={containers[STAGING_AREA_ID] || []}
              albumName={albumName}
              uploadProgress={uploadProgress}
              isUploading={loadingPhotos}
              onRemovePhoto={handleRemoveFromStaging}
            />

            <QuickDropZones
              listings={listings.filter(l => !l.error)}
              onQuickDrop={(photoId, targetReleaseId) => {
                // This function is handled by the drag/drop hook now
                console.log(`Quick drop: ${photoId} -> ${targetReleaseId}`);
              }}
            />

            {containers[STAGING_AREA_ID]?.length > 0 && (
              <div className="text-center mt-6">
                <button
                  onClick={handleMatchPhotos}
                  disabled={isMatching}
                  className={`px-6 py-3 font-semibold rounded-lg shadow-md transition ${
                    isMatching
                      ? "bg-blue-400 cursor-wait"
                      : "bg-blue-600 hover:bg-blue-700"
                  } text-white`}
                >
                  {isMatching ? "Matching…" : "Match Photos"}
                </button>
              </div>
            )}

            <div className="text-center mt-6">
              <button
                onClick={handleSubmitAll}
                disabled={isSubmitting}
                className={`px-6 py-3 font-semibold rounded-lg shadow-md transition ${
                  isSubmitting
                    ? "bg-red-400 cursor-wait"
                    : "bg-red-600 hover:bg-red-700"
                } text-white`}
              >
                {isSubmitting ? "Submitting…" : "Submit Listings to eBay"}
              </button>
            </div>
          </>
        )}

        {batchId && (
          <div className="text-center mt-4">
            <ClearBatchButton
              batchId={batchId}
              onSuccess={() => {
                setListings([]);
                setContainers({ [STAGING_AREA_ID]: [] });
                setBatchId(null);
                setSubmissionSuccess(false);
                setToastType("success");
                setToastMessage("🧹 Batch cleared.");
                pasteRef.current?.clearInput(); // ✅ Clears Discogs input box
              }}
            />
          </div>
        )}
      </div>

      {toastMessage && (
        <div
          className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-xl shadow-lg text-sm font-medium z-50 transition-all duration-300 ${
            toastType === "success"
              ? "bg-green-600 text-white"
              : "bg-red-600 text-white"
          }`}
        >
          {toastMessage}
        </div>
      )}

      <DragOverlay>
        {activePhoto && <DraggableImage image={activePhoto} isOverlay />}
      </DragOverlay>
    </DndContext>
  );
};

export default BatchListingManager;
